import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RowActions } from "@/components/dashboard/RowActions";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { UserPlus, KeyRound } from "lucide-react";
import usePagination from "@/lib/usePagination";
import TablePagination from "@/components/dashboard/TablePagination";

const rolePill = { admin: "bg-navy text-gold hover:bg-navy", client: "bg-gold text-white hover:bg-gold" };
const EMPTY = { name: "", email: "", role: "client", password: "", client_id: "" };

export default function Invite() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const { page, setPage, totalPages, pageItems, rangeStart, rangeEnd, total } = usePagination(users, 20);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/clients")]);
      setUsers(u.data.filter(x => x.role === "admin" || x.role === "client"));
      setClients(c.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const genPassword = () => {
    const s = Math.random().toString(36).slice(2, 10) + "!" + Math.floor(Math.random()*99);
    setF((x) => ({ ...x, password: s }));
  };

  const clientNameOf = (id) => clients.find(c => c.client_id === id)?.name || "—";

  const create = async () => {
    if (!f.name || !f.email || !f.password) return toast.error("All fields are required");
    if (f.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (f.role === "client" && !f.client_id) return toast.error("Please select the client company for this user");
    setSaving(true);
    try {
      await api.post("/auth/admin/invite", f);
      toast.success(`${f.role === "admin" ? "Admin" : "Client"} account created for ${f.email}`);
      setOpen(false);
      setF(EMPTY);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create account");
    } finally { setSaving(false); }
  };

  const del = async (uid) => {
    try { await api.delete(`/users/${uid}`); toast.success("Account deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const genResetPw = () => setResetPw(Math.random().toString(36).slice(2, 10) + "!" + Math.floor(Math.random() * 99));
  const submitReset = async () => {
    if (!resetTarget) return;
    if (resetPw.length < 8) return toast.error("Password must be at least 8 characters");
    setResetting(true);
    try {
      await api.post(`/auth/admin/reset-password/${resetTarget.user_id}`, { new_password: resetPw });
      toast.success(`Password reset for ${resetTarget.email}. Share the new password with them.`);
      setResetTarget(null); setResetPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    } finally { setResetting(false); }
  };

  return (
    <div className="space-y-8" data-testid="invite-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-navy/50">Admin</div>
          <h1 className="serif-display text-4xl mt-2">Staff &amp; Client accounts</h1>
          <p className="text-navy/60 mt-2">Create password-based accounts. Client accounts are linked to a client company. Users will be forced to reset their password on first sign-in.</p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="invite-new-btn"><UserPlus size={14}/> New account</button>
      </div>

      <div className="bg-white border border-navy/10">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-navy/50 py-8">No staff or client accounts yet.</TableCell></TableRow>}
            {pageItems.map((u) => (
              <TableRow key={u.user_id}>
                <TableCell className="serif-display">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge className={rolePill[u.role]}>{u.role}</Badge></TableCell>
                <TableCell>{u.role === "client" ? clientNameOf(u.client_id) : "—"}</TableCell>
                <TableCell className="text-navy/60 text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 justify-end">
                    {u.user_id !== me?.user_id && (
                      <>
                        <button
                          onClick={() => { setResetTarget(u); setResetPw(""); }}
                          className="text-navy/50 hover:text-gold p-1.5"
                          aria-label="Reset password"
                          data-testid={`reset-pw-${u.user_id}`}
                        ><KeyRound size={14}/></button>
                        <RowActions onDelete={() => del(u.user_id)} deleteTestid={`delete-invite-${u.user_id}`} itemLabel={u.email}/>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={page} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(totalPages, p + 1))} testidPrefix="invite-table" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="serif-display text-2xl">Create staff / client account</DialogTitle>
            <DialogDescription>The user will be asked to change this temporary password on first sign-in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Full name</Label><Input value={f.name} onChange={(e)=>setF({...f, name: e.target.value})} data-testid="invite-name"/></div>
            <div><Label>Company email</Label><Input type="email" value={f.email} onChange={(e)=>setF({...f, email: e.target.value.toLowerCase()})} data-testid="invite-email"/></div>
            <div>
              <Label>Role</Label>
              <Select value={f.role} onValueChange={(v)=>setF({...f, role: v, client_id: v === "admin" ? "" : f.client_id})}>
                <SelectTrigger data-testid="invite-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client (payroll & manpower dashboards)</SelectItem>
                  <SelectItem value="admin">Admin (full internal access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {f.role === "client" && (
              <div>
                <Label>Client company</Label>
                <Select value={f.client_id} onValueChange={(v)=>setF({...f, client_id: v})}>
                  <SelectTrigger data-testid="invite-client-company"><SelectValue placeholder="Select client company"/></SelectTrigger>
                  <SelectContent>
                    {clients.length === 0 && <div className="p-2 text-sm text-navy/60">Create a client company first (Clients page).</div>}
                    {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-xs text-navy/50 mt-1">The user will only see this company's payroll and manpower data.</div>
              </div>
            )}
            <div>
              <Label>Temporary password</Label>
              <div className="flex gap-2">
                <PasswordInput value={f.password} onChange={(e)=>setF({...f, password: e.target.value})} data-testid="invite-password" eyeTestid="invite-password-eye" className="flex-1"/>
                <button type="button" className="btn-outline !px-3 !py-2 !text-[11px]" onClick={genPassword}>Generate</button>
              </div>
              <div className="text-xs text-navy/50 mt-1">Share this with the user out-of-band. They will change it on first sign-in.</div>
            </div>
          </div>
          <DialogFooter>
            <button className="btn-outline" onClick={()=>setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={create} disabled={saving} data-testid="invite-submit">{saving ? "Creating..." : "Create account"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(o)=>!o && setResetTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="serif-display text-2xl">Reset password</DialogTitle>
            <DialogDescription>Issue a new temporary password for <span className="text-navy font-semibold">{resetTarget?.email}</span>. They will be forced to change it on next sign-in, and all their active sessions will be signed out.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New password</Label>
              <div className="flex gap-2">
                <PasswordInput value={resetPw} onChange={(e)=>setResetPw(e.target.value)} data-testid="reset-pw-input" eyeTestid="reset-pw-eye" className="flex-1"/>
                <button type="button" className="btn-outline !px-3 !py-2 !text-[11px]" onClick={genResetPw}>Generate</button>
              </div>
              <div className="text-xs text-navy/50 mt-1">Share this with the user out-of-band. They must change it on first sign-in.</div>
            </div>
          </div>
          <DialogFooter>
            <button className="btn-outline" onClick={()=>setResetTarget(null)}>Cancel</button>
            <button className="btn-primary" onClick={submitReset} disabled={resetting} data-testid="reset-pw-submit">{resetting ? "Resetting..." : "Reset password"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
